import React, { useState, useEffect, useRef, useMemo } from 'react';
import logo from '../logo.png';

const API_BASE = '/api';

const STATUS_CONFIG = {
  completed: { label: 'Completed', bg: 'rgba(72, 187, 120, 0.15)', color: '#48bb78', icon: '✓' },
  failed: { label: 'Failed', bg: 'rgba(245, 101, 101, 0.15)', color: '#fc8181', icon: '✕' },
  image_failed: { label: 'Image Failed', bg: 'rgba(245, 101, 101, 0.15)', color: '#fc8181', icon: '✕' },
  video_failed: { label: 'Video Failed', bg: 'rgba(245, 101, 101, 0.15)', color: '#fc8181', icon: '✕' },
  processing: { label: 'Processing', bg: 'rgba(102, 126, 234, 0.15)', color: '#667eea', icon: '⟳' },
  pending: { label: 'Pending', bg: 'rgba(160, 174, 192, 0.15)', color: '#a0aec0', icon: '○' },
};

function getStatusStyle(status) {
  if (status === 'completed') return STATUS_CONFIG.completed;
  if (status.includes('fail')) return STATUS_CONFIG[status] || STATUS_CONFIG.failed;
  if (status.includes('process') || status.includes('generat')) return STATUS_CONFIG.processing;
  return STATUS_CONFIG.pending;
}

function formatDate(dateStr) {
  if (!dateStr) return 'Just now';
  if (!dateStr.endsWith('Z') && !dateStr.includes('+')) dateStr += 'Z';
  return new Date(dateStr).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function AdminDashboard() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [authHeader, setAuthHeader] = useState('');

  const [jobs, setJobs] = useState([]);
  const [initialLoading, setInitialLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedJob, setExpandedJob] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const authRef = useRef(authHeader);
  useEffect(() => { authRef.current = authHeader; }, [authHeader]);

  const fetchJobs = async (credentialsHeader, isBackgroundUpdate = false) => {
    if (!isBackgroundUpdate) setInitialLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/jobs`, {
        headers: { 'Authorization': credentialsHeader }
      });

      if (res.status === 401) throw new Error("Incorrect username or password");
      if (!res.ok) throw new Error("Failed to fetch jobs");

      const data = await res.json();
      setJobs(data.jobs || []);
      setIsAuthenticated(true);
      setAuthHeader(credentialsHeader);
      setLastUpdated(new Date());
    } catch (err) {
      if (!isBackgroundUpdate) {
        setError(err.message);
        setIsAuthenticated(false);
      }
    } finally {
      if (!isBackgroundUpdate) setInitialLoading(false);
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    const encodedCredentials = 'Basic ' + btoa(`${username}:${password}`);
    fetchJobs(encodedCredentials, false);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setAuthHeader('');
    setUsername('');
    setPassword('');
    setJobs([]);
    setSearchQuery('');
    setStatusFilter('all');
    setExpandedJob(null);
    setLastUpdated(null);
  };

  // Auto-refresh timer (polls every 3 seconds)
  useEffect(() => {
    if (!isAuthenticated) return;
    const interval = setInterval(() => {
      if (authRef.current) fetchJobs(authRef.current, true);
    }, 3000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // Stats
  const stats = useMemo(() => {
    const total = jobs.length;
    const completed = jobs.filter(j => j.status === 'completed').length;
    const failed = jobs.filter(j => j.status.includes('fail')).length;
    const processing = total - completed - failed;
    return { total, completed, failed, processing };
  }, [jobs]);

  // Filtered jobs
  const filteredJobs = useMemo(() => {
    return jobs.filter(job => {
      const matchesSearch = !searchQuery ||
        (job.user_email && job.user_email.toLowerCase().includes(searchQuery.toLowerCase())) ||
        job.job_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (job.sport && job.sport.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesStatus = statusFilter === 'all' ||
        (statusFilter === 'completed' && job.status === 'completed') ||
        (statusFilter === 'failed' && job.status.includes('fail')) ||
        (statusFilter === 'processing' && !['completed'].includes(job.status) && !job.status.includes('fail'));

      return matchesSearch && matchesStatus;
    });
  }, [jobs, searchQuery, statusFilter]);

  // ── Login Screen ──
  if (!isAuthenticated) {
    return (
      <div className="app-wrapper">
        <div className="screen" style={{ maxWidth: '400px', padding: '40px 20px', margin: '0 auto' }}>
          <img src={logo} alt="Pixperience" className="logo" style={{ width: '150px', marginBottom: '20px' }} />
          <h1 style={{ fontSize: '24px', marginBottom: '8px' }}>Admin Portal</h1>
          <p className="subtitle" style={{ marginBottom: '24px' }}>Sign in to manage your video queue</p>
          {error && <p className="error-text">{error}</p>}
          <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '0', width: '100%' }}>
            <input
              type="text"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="input-field"
              required
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-field"
              required
            />
            <button type="submit" className="btn" disabled={initialLoading}>
              {initialLoading ? "Verifying..." : "Secure Login"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ── Dashboard Screen ──
  return (
    <div className="app-wrapper admin-dashboard">

      {/* Header */}
      <div className="admin-header">
        <div className="admin-header-left">
          <img src={logo} alt="Logo" className="logo" style={{ marginBottom: 0, padding: '8px 16px', maxHeight: '50px' }} />
          <div>
            <h2 className="admin-title">Live Queue</h2>
            <p className="admin-subtitle">
              <span className="pulse-dot" />
              Auto-updating
              {lastUpdated && <span> · {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>}
            </p>
          </div>
        </div>
        <button onClick={handleLogout} className="btn-logout">Logout</button>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card" onClick={() => setStatusFilter('all')}>
          <div className="stat-number">{stats.total}</div>
          <div className="stat-label">Total Jobs</div>
        </div>
        <div className="stat-card stat-completed" onClick={() => setStatusFilter('completed')}>
          <div className="stat-number" style={{ color: '#48bb78' }}>{stats.completed}</div>
          <div className="stat-label">Completed</div>
        </div>
        <div className="stat-card stat-processing" onClick={() => setStatusFilter('processing')}>
          <div className="stat-number" style={{ color: '#667eea' }}>{stats.processing}</div>
          <div className="stat-label">Processing</div>
        </div>
        <div className="stat-card stat-failed" onClick={() => setStatusFilter('failed')}>
          <div className="stat-number" style={{ color: '#fc8181' }}>{stats.failed}</div>
          <div className="stat-label">Failed</div>
        </div>
      </div>

      {/* Search & Filter Bar */}
      <div className="admin-toolbar">
        <input
          type="text"
          placeholder="Search by email, ID, or sport..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="admin-search"
        />
        <div className="filter-pills">
          {['all', 'completed', 'processing', 'failed'].map(f => (
            <button
              key={f}
              className={`filter-pill ${statusFilter === f ? 'active' : ''}`}
              onClick={() => setStatusFilter(f)}
            >
              {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Jobs List */}
      {initialLoading ? <div className="loader" style={{ marginTop: '50px' }}></div> : (
        <>
          {/* Desktop Table */}
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>User Details</th>
                  <th>Sport</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Video</th>
                </tr>
              </thead>
              <tbody>
                {filteredJobs.map(job => {
                  const statusStyle = getStatusStyle(job.status);
                  const isExpanded = expandedJob === job.job_id;

                  return (
                    <React.Fragment key={job.job_id}>
                      <tr
                        className={`admin-row ${isExpanded ? 'expanded' : ''}`}
                        onClick={() => setExpandedJob(isExpanded ? null : job.job_id)}
                      >
                        {/* Image */}
                        <td>
                          {job.image_url ? (
                            <a href={job.image_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}>
                              <img src={job.image_url} alt="Generated" className="admin-thumb" />
                            </a>
                          ) : (
                            <div className="admin-thumb-placeholder">Wait</div>
                          )}
                        </td>

                        {/* User Details */}
                        <td>
                          <div className="admin-email">
                            {job.user_email && job.user_email !== 'None' ? job.user_email : 'No email'}
                          </div>
                          <div className="admin-job-id">ID: {job.job_id.split('-')[0]}</div>
                          <div className="admin-date">{formatDate(job.created_at)}</div>
                        </td>

                        {/* Sport */}
                        <td>
                          <span className="admin-sport">{job.sport}</span>
                        </td>

                        {/* Status */}
                        <td>
                          <span className="admin-badge" style={{ background: statusStyle.bg, color: statusStyle.color }}>
                            <span className="admin-badge-icon">{statusStyle.icon}</span>
                            {job.status.replace(/_/g, ' ')}
                          </span>
                          {job.status.includes('fail') && job.message && (
                            <div className="admin-error-hint">{job.message}</div>
                          )}
                        </td>

                        {/* Video */}
                        <td style={{ textAlign: 'right' }}>
                          {job.status === 'completed' && job.download_url ? (
                            <a
                              href={job.download_url}
                              target="_blank"
                              rel="noreferrer"
                              className="btn-watch"
                              onClick={e => e.stopPropagation()}
                            >
                              Watch →
                            </a>
                          ) : (
                            <span className="admin-processing-label">
                              {job.status.includes('fail') ? 'Failed' : 'Processing...'}
                            </span>
                          )}
                        </td>
                      </tr>

                      {/* Expanded Detail Row */}
                      {isExpanded && (
                        <tr className="admin-detail-row">
                          <td colSpan="5">
                            <div className="admin-detail-content">
                              <div className="admin-detail-item">
                                <span className="admin-detail-label">Full Job ID</span>
                                <span className="admin-detail-value">{job.job_id}</span>
                              </div>
                              <div className="admin-detail-item">
                                <span className="admin-detail-label">Email</span>
                                <span className="admin-detail-value">{job.user_email || 'N/A'}</span>
                              </div>
                              <div className="admin-detail-item">
                                <span className="admin-detail-label">Created</span>
                                <span className="admin-detail-value">{formatDate(job.created_at)}</span>
                              </div>
                              <div className="admin-detail-item">
                                <span className="admin-detail-label">Status</span>
                                <span className="admin-detail-value">{job.status}</span>
                              </div>
                              {job.message && (
                                <div className="admin-detail-item">
                                  <span className="admin-detail-label">Message</span>
                                  <span className="admin-detail-value admin-detail-error">{job.message}</span>
                                </div>
                              )}
                              {job.download_url && (
                                <div className="admin-detail-item">
                                  <span className="admin-detail-label">Video URL</span>
                                  <a href={job.download_url} target="_blank" rel="noreferrer" className="admin-detail-link">{job.download_url}</a>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}

                {filteredJobs.length === 0 && (
                  <tr>
                    <td colSpan="5" className="admin-empty">
                      {jobs.length === 0 ? 'No jobs in the queue yet.' : 'No jobs match your search.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Card Layout */}
          <div className="admin-cards-mobile">
            {filteredJobs.map(job => {
              const statusStyle = getStatusStyle(job.status);
              const isExpanded = expandedJob === job.job_id;

              return (
                <div
                  key={job.job_id}
                  className={`admin-card ${isExpanded ? 'expanded' : ''}`}
                  onClick={() => setExpandedJob(isExpanded ? null : job.job_id)}
                >
                  <div className="admin-card-top">
                    {job.image_url ? (
                      <a href={job.image_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}>
                        <img src={job.image_url} alt="Generated" className="admin-thumb" />
                      </a>
                    ) : (
                      <div className="admin-thumb-placeholder">Wait</div>
                    )}
                    <div className="admin-card-info">
                      <div className="admin-email">
                        {job.user_email && job.user_email !== 'None' ? job.user_email : 'No email'}
                      </div>
                      <div className="admin-job-id">ID: {job.job_id.split('-')[0]}</div>
                      <div className="admin-date">{formatDate(job.created_at)}</div>
                    </div>
                  </div>
                  <div className="admin-card-bottom">
                    <span className="admin-sport">{job.sport}</span>
                    <span className="admin-badge" style={{ background: statusStyle.bg, color: statusStyle.color }}>
                      <span className="admin-badge-icon">{statusStyle.icon}</span>
                      {job.status.replace(/_/g, ' ')}
                    </span>
                    {job.status === 'completed' && job.download_url ? (
                      <a href={job.download_url} target="_blank" rel="noreferrer" className="btn-watch" onClick={e => e.stopPropagation()}>Watch →</a>
                    ) : (
                      <span className="admin-processing-label">
                        {job.status.includes('fail') ? 'Failed' : 'Processing...'}
                      </span>
                    )}
                  </div>

                  {isExpanded && (
                    <div className="admin-detail-content" style={{ marginTop: '12px', borderTop: '1px solid #333' , paddingTop: '12px' }}>
                      <div className="admin-detail-item">
                        <span className="admin-detail-label">Full Job ID</span>
                        <span className="admin-detail-value">{job.job_id}</span>
                      </div>
                      {job.message && (
                        <div className="admin-detail-item">
                          <span className="admin-detail-label">Message</span>
                          <span className="admin-detail-value admin-detail-error">{job.message}</span>
                        </div>
                      )}
                      {job.download_url && (
                        <div className="admin-detail-item">
                          <span className="admin-detail-label">Video URL</span>
                          <a href={job.download_url} target="_blank" rel="noreferrer" className="admin-detail-link">{job.download_url}</a>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {filteredJobs.length === 0 && (
              <div className="admin-empty" style={{ padding: '40px', textAlign: 'center' }}>
                {jobs.length === 0 ? 'No jobs in the queue yet.' : 'No jobs match your search.'}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
